//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useEffect, useState } from 'react';

// === API ===
import { prokeyAuditQCEdit, removeListener, onTenants, requestTenants, requestAuditModes, onAuditModes } from 'api/prokey/prokey.websocket';

// === STYLES ===
import { useTheme } from '@mui/material/styles';
import MuiAlert from '@mui/material/Alert';
import { Autocomplete } from '@mui/material/index';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  InputLabel,
} from '@mui/material';

//-----------------------------------------------------------------
function Alert(props) {
  //-----------------------------------------------------------------
  return <MuiAlert elevation={6} variant='filled' {...props} />;
}

//#endregion

//-----------------------------------------------------------------
function EditAuditQCDialog(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { field } = props;

  const theme = useTheme();

  const [ tenant         , setTenant]           = useState( field.Tenant );
  const [ auditMode      , setAuditMode ]       = useState( field.AuditMode );
  const [ auditType      , setAuditType ]       = useState( field.AuditType );
  const [ fieldName      , setFieldName ]       = useState( field.FieldName );
  const [ fieldValue     , setFieldValue ]      = useState( field.FieldValue );
  const [ auditReturnCode, setAuditReturnCode ] = useState( field.AuditReturnCode );
  const [ auditFrequency , setAuditFrequency ]  = useState( field.AuditFrequency );
  const [ auditCount     , setAuditCount ]      = useState( field.AuditCount );

  const [ tenants        , setTenants ]         = useState([]);
  const [ auditModes     , setAuditModes ]      = useState([]);

  const auditTypeOptions       = [ 'Sync', 'Async' ];
  const auditReturnCodeOptions = [ 'Normal', 'Forward' ];

  const [ show_alert , setShowAlert ] = useState( false );
  const [ alert_text , setAlertText ] = useState( '' );

  //#endregion

  //#region [STYLES]
  
   // === STYLE ===
  const _styles = {
    container: {
    },
    form: {
    },
  }

  // === ATTRIBUTES ===
  const
    CONTAINER = {
      sx : _styles.container,
    },
    FORM = {
      sx        : _styles.form ,
      fullWidth : true,
      margin    : 'dense',
    },
    DIALOG = {
      sx             : _styles.container,
      open           : props.showDialog,
      onClose        : handleClose,
      ariaLabelledby : 'form-dialog-title',
    },
    TENANT = {
      id           : 'tenant',
      options      :  tenants,
      value        :  tenant,
      onChange     : (event, inputValue) => { setTenant( inputValue || '' ) },
      autoComplete : 'off',
      freeSolo     : true,
      renderInput  : (params) => ( <TextField {...params} label='Tenant' /> ),
      name         : 'edit_tenant_input'
    },   
    AUDIT_MODE = {
      id           : 'audit-mode',
      options      :  auditModes, 
      value        :  auditMode,
      autoComplete : 'off',
      freeSolo     : true,
      onChange     : (event, inputValue) => { setAuditMode( inputValue || '' ) },
      renderInput  : (params) => ( <TextField {...params} label='Audit Mode' /> ),
      name        : 'edit_audit_mode_input',
    },
    AUDIT_TYPE_LABEL = {
      id           :'audit-type', 
      children     :'Audit type',
    },
    AUDIT_TYPE = {
      id           : 'audit-type',
      labelId      : 'audit-type',
      value        :  auditType,
      autoComplete : 'off',
      input        : <OutlinedInput label='Audit Type'/>,
      onChange     : (event) => { setAuditType(event.target.value); },
      children     : auditTypeOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>)),
      name         : 'edit_audit_type_select',
    },
    FIELD_NAME = {
      id           : 'field-name',
      label        : 'Field Name',
      value        : fieldName,
      type         : 'text',
      margin       : 'dense',
      autoComplete : 'off',
      fullWidth    : true,
      onChange     : (event) => { setFieldName(event.target.value); },
      name         : 'edit_field_name_input',
    },
    FIELD_VALUE = {
      id           : 'field-value',
      label        : 'Field Value',
      autoComplete : 'off',
      fullWidth    : true,
      value        : fieldValue,
      type         : 'text',
      margin       : 'dense',
      onChange     : (event) => { setFieldValue(event.target.value); },
      name         : 'edit_field_value_input',
    },
    AUDIT_RETURN_CODE_LABEL = {
      id           :'audit-return-code',
      children     :'Audit Return Code',
    },
    AUDIT_RETURN_CODE = {
      id           : 'audit-return-code',
      labelId      : 'audit-return-code',
      value        :  auditReturnCode,
      autoComplete : 'off',
      input        : <OutlinedInput label='Audit Return Code'/>,
      onChange     : (event) => { setAuditReturnCode(event.target.value); },
      children     : auditReturnCodeOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>)),
      name         : 'edit_audit_return_code_select',
      },
    AUDIT_FREQUENCY = {
      id           : 'audit-frequency',
      label        : 'Audit Frequency',
      value        :  auditFrequency,
      type         : 'text',
      margin       : 'dense',
      autoComplete : 'off',
      fullWidth    : true,
      onChange     : (event) => { setAuditFrequency(event.target.value); },
      name         : 'edit_audit_frequency_input',
    },
    AUDIT_COUNT = {
      id           : 'autid-count',
      label        : 'Audit Count',  
      value        :  auditCount,
      type         : 'text',
      margin       : 'dense',
      autoComplete : 'off',
      fullWidth    : true,
      onChange     : (event) => { setAuditCount(event.target.value); },
      name         : 'edit_audit_count_input',
    },
    
    BUTTON_CLOSE = {
      color    : 'primary',
      onClick  : handleClose,
      children : [ 'Close' ],
      name     : 'close_edit_qc_dialog_btn',
    },    
    BUTTON_SUBMIT = {
      color    : 'primary',
      onClick  : handleSubmit,
      children : [ 'Submit' ],
      name     : 'submit_edit_dialog_qc_dialog',
    };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    setTenant(field.Tenant)
    setAuditMode( field.AuditMode );
    setAuditType ( field.AuditType );
    setFieldName   ( field.FieldName );
    setFieldValue ( field.FieldValue );
    setAuditReturnCode( field.AuditReturnCode );
    setAuditFrequency ( field.AuditFrequency );
    setAuditCount  ( field.AuditCount );

  },[field] );
 
  //#endregion

  //#region [MODIFY]

  //-----------------------------------------------------------------
  async function  editAuditQCTable() {
    //-----------------------------------------------------------------
    try {
      let error_text = '';
      if ( !tenant  ) error_text        += ', Tenant must be filled';
      if ( !auditMode ) error_text      += ', Audit Mode  must be filled';
      if ( !auditType ) error_text      += ', Audit Type  must be filled';
      if ( !fieldName ) error_text      += ', Field Name  must be filled';
      if ( !fieldValue ) error_text     += ', Field Value must be filled';
      if ( !auditFrequency ) error_text += ', Audit Frequency must be filled';
      if ( auditCount === null || auditCount === undefined) { error_text += ', Audit Count must be filled' };

      if ( !Number.isInteger(Number(auditFrequency)) ) error_text += ', Audit Frequency  must be an integer';
      if ( !Number.isInteger(Number(auditCount)) ) error_text     += ', Audit Count  must be an integer';

      if (error_text) {
        setAlertText(error_text.substring(2));
        setShowAlert(true);
        return false;
      }
     
      let field_data = {
        tenant,
        auditMode,
        auditType,
        fieldName,
        fieldValue,
        auditReturnCode,
        auditFrequency,
        auditCount,  
      };
     
      prokeyAuditQCEdit(field.Id, field_data)   

      return true;
    } catch (error) {
      console.log( error );
    }
  }

  //-----------------------------------------------------------------
  function resetData() {
    //-----------------------------------------------------------------
    setTenant         ( field.Tenant );
    setAuditMode      ( field.AuditMode );
    setAuditType      ( field.AuditType );
    setFieldName      ( field.FieldName );
    setFieldValue     ( field.FieldValue );
    setAuditReturnCode( field.AuditReturnCode );
    setAuditFrequency ( field.AuditFrequency );
    setAuditCount     ( field.AuditCount );
  };

  //#endregion
  
  //#region [EVENTS]
  
  useEffect(() => {
    //-----------------------------------------------------------------
    function handleTenants( data ) {
      //-----------------------------------------------------------------
      const tenantList = data.map(tenant => tenant || 'default')
        setTenants( tenantList );
    };
     //-----------------------------------------------------------------
     function handleAuditModes( data ) {
      //-----------------------------------------------------------------
      setAuditModes( data );
    };
   
    onTenants( handleTenants );
    onAuditModes( handleAuditModes );

    requestTenants();  
    requestAuditModes();
    
     return () => {
      removeListener( 'TENANT-REQUEST', handleTenants );
      removeListener( 'PROKEY-AUDIT-MODE-REQUEST', handleAuditModes );   
    }
  }, []);

  //-----------------------------------------------------------------
  async function handleSubmit( event ) {
    //-----------------------------------------------------------------
   if(await editAuditQCTable()) {
    props.setShowDialog(false);
   }
  };

  //-----------------------------------------------------------------
  function handleClose() {
    //-----------------------------------------------------------------
    props.setShowDialog(false);
    handleDismissAlert();
    resetData();
  };

  //-----------------------------------------------------------------
  function handleDismissAlert() {
    //-----------------------------------------------------------------
    setShowAlert(false);
    setAlertText('');
  };
   
  //-----------------------------------------------------------------
  function handleErrClose(event, reason) {
    //-----------------------------------------------------------------
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert(false);
  };

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function displayError() {
    //-----------------------------------------------------------------
    return (
        <Snackbar open={show_alert} autoHideDuration={4000} onClose={handleErrClose} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <div>
          <Alert onClose={handleErrClose} severity='error'>
            {alert_text}
          </Alert>
        </div>
      </Snackbar>
    );
  };
  
  //-----------------------------------------------------------------
  function displayAuditQCModal() {
    //-----------------------------------------------------------------
    if( !props.showDialog ) return null;
    return (
      <div { ...CONTAINER      } >
        <Dialog { ...DIALOG    } >
          <DialogTitle id='form-dialog-title' variant='primary' sx={{ backgroundColor: theme.palette.background.secondary[theme.palette.mode] }} >Edit Audit QC</DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.secondary[theme.palette.mode] }} >
            <DialogContentText>Enter information</DialogContentText>
            <FormControl    { ...FORM    } >
              <Autocomplete { ...TENANT  } />
            </FormControl>
            <FormControl    { ...FORM          } >
              <Autocomplete { ...AUDIT_MODE    } />
            </FormControl>
            <FormControl  { ...FORM                } >
              <InputLabel { ...AUDIT_TYPE_LABEL    } />
              <Select     { ...AUDIT_TYPE          } />
            </FormControl>
            <TextField { ...FIELD_NAME          } />
            <TextField { ...FIELD_VALUE         } />
            <FormControl  { ...FORM                       } >
              <InputLabel { ...AUDIT_RETURN_CODE_LABEL    } />
              <Select { ...AUDIT_RETURN_CODE              } />
            </FormControl> 
            <TextField { ...AUDIT_FREQUENCY     } />
            <TextField { ...AUDIT_COUNT         } />
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.secondary[theme.palette.mode] }} >
            <Button { ...BUTTON_CLOSE    } />
            <Button { ...BUTTON_SUBMIT   } />
          </DialogActions>
        </Dialog>
        {displayError()}
      </div>
    );
  }

  //#endregion

  return displayAuditQCModal();
}

export { EditAuditQCDialog };
